import { useState } from 'react'
import styles from './ProjectsTableView.module.css'
import { ConfirmationModal } from '../modals'

/**
 * ProjectsTableView - Component for displaying projects in a table format with association details
 * 
 * Follows SOLID principles:
 * - Single Responsibility: Only handles project table display and interactions
 * - Open/Closed: Extensible through props without modification
 * - Interface Segregation: Focused interface for project table operations
 */
export default function ProjectsTableView({ 
    projects = [], 
    onRemoveDirectAccess, 
    loading = false,
    member = null 
}) {
    const [confirmingRemoval, setConfirmingRemoval] = useState(null)

    const handleRemoveClick = (project) => {
        setConfirmingRemoval(project)
    }

    const handleConfirmRemove = async () => {
        if (!confirmingRemoval || !member) return

        try {
            await onRemoveDirectAccess(confirmingRemoval.id, member.id)
            setConfirmingRemoval(null)
        } catch (error) {
            console.error('Failed to remove direct project access:', error)
            // Error handling is delegated to parent component
        }
    }

    const handleCancelRemove = () => {
        setConfirmingRemoval(null)
    }

    const getConfirmationMessage = () => {
        if (!confirmingRemoval || !member) return ''
        
        const memberName = member.full_name || member.display_name || member.email
        const projectName = confirmingRemoval.name
        
        return `Are you sure you want to remove direct access for ${memberName} from ${projectName}?`
    }

    const getAssociationType = (project) => {
        const associations = []
        
        if (project.direct_access) {
            associations.push('Direct')
        }
        
        if (project.group_accesses && project.group_accesses.length > 0) {
            const groupNames = project.group_accesses.map(access => access.group_name).join(', ')
            associations.push(`Via Groups: ${groupNames}`)
        }
        
        return associations.join(' • ')
    }

    const canRemoveDirectAccess = (project) => {
        return project.direct_access
    }

    if (loading) {
        return (
            <div className={styles.loadingState}>
                <p>Loading projects...</p>
            </div>
        )
    }

    if (projects.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>This member has no project access</p>
            </div>
        )
    }

    return (
        <>
            <div className={styles.tableContainer}>
                <table className={styles.projectsTable}>
                    <thead>
                        <tr>
                            <th>Project Name</th>
                            <th>Client</th>
                            <th>Access Via</th>
                            <th>Role</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {projects.map((project) => (
                            <tr key={project.id} className={styles.tableRow}>
                                <td>
                                    <div className={styles.projectInfo}>
                                        <span className={styles.projectName}>{project.name}</span>
                                        {project.status === 'inactive' && (
                                            <span className={styles.inactiveTag}>Inactive</span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <span className={styles.clientName}>
                                        {project.client_name || 'No client'}
                                    </span>
                                </td>
                                <td>
                                    <span className={styles.associationType}>
                                        {getAssociationType(project)}
                                    </span>
                                </td>
                                <td>
                                    <span className={styles.role}>
                                        {project.role || 'Member'}
                                    </span>
                                </td>
                                <td>
                                    <div className={styles.actions}>
                                        {canRemoveDirectAccess(project) ? (
                                            <button
                                                className={styles.removeButton}
                                                onClick={() => handleRemoveClick(project)}
                                                aria-label={`Remove direct access from ${project.name}`}
                                                title={`Remove direct access from ${project.name}`}
                                            >
                                                Remove Direct Access
                                            </button>
                                        ) : (
                                            <span className={styles.noAction}>
                                                {project.group_accesses && project.group_accesses.length > 0 
                                                    ? 'Access via groups only' 
                                                    : 'No direct access'}
                                            </span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmationModal
                isOpen={!!confirmingRemoval}
                onClose={handleCancelRemove}
                onConfirm={handleConfirmRemove}
                title="Remove Direct Project Access"
                message={getConfirmationMessage()}
                confirmText="Remove Access"
                cancelText="Cancel"
                confirmButtonStyle="danger"
            />
        </>
    )
}